/**
 * 5897 — Previsões de vendas (área interna). Formato do snapshot gravado pelo
 * cron do producer-analytics (services/previsoes/calculo.ts).
 */

// Permissões (cadastradas no painel, módulo PV)
export const PREVISOES_PERM = {
  VISUALIZAR: 'PV1',
}

export interface PrevisaoEvento {
  id: number
  nome: string
  cidade: string
  local: string | null
  estilo: string
  data: string
  data_completa: string
  /** dias até o evento */
  dias: number
  pagos: number
  /** valor de face vendido */
  receita: number
  gmv: number
  r7: number
  r7a: number
  p10: number
  p50: number
  p90: number
  p50_7d_atras: number | null
  longo: boolean
  confianca: 'boa' | 'média' | 'baixa'
  /** eventos anteriores do produtor no histórico */
  hist: number
  alertas: string[]
  comparaveis_qualidade: 'forte' | 'moderada' | 'insuficiente'
}

export interface Comparavel {
  id: number
  nome: string
  data: string
  cidade: string
  pagos: number
  escore: number
  edicao_anterior: boolean
  vendido_mesmo_ponto: number
}

export interface PrevisaoEventoDetalhe extends PrevisaoEvento {
  comparaveis: Comparavel[]
  /** k (dias até o evento, 0..60) → [P25, mediana, P75] da fração vendida dos comparáveis */
  banda: Record<string, [number, number, number]> | null
  /** [dias até o evento, pagos acumulados] */
  serie: Array<[number, number]>
}

export interface PrevisaoLote {
  evento_id: number
  evento: string
  item: string
  lote: string
  unidade: 'ingresso' | 'setor'
  qtd: number
  vend: number
  saldo: number
  ritmo: number
  dias: number
  p7: number
  pev: number | null
  janela: string
  leitura: string
}

export interface FaixaGmv {
  p50: number
  p10: number
  p90: number
}

export interface TopGmv {
  nome: string
  data: string
  gmv: number
  face: number
  ja_vendido?: number
  ja_em_venda?: boolean
}

export interface GmvHorizonte {
  por_data_evento: FaixaGmv & { ja_vendido: number; eventos: number }
  por_data_venda: FaixaGmv
  /** 'blend' (até 14 dias): soma das projeções por evento combinada com o nível dos últimos 7 dias; 'eventos': só a soma */
  venda_metodo?: 'blend' | 'nivel' | 'eventos'
  venda_erro_tipico?: number | null
  ultimos_h_dias: number
  ano_anterior_venda: number
  top: TopGmv[]
  face: {
    evento_p50: number
    evento_p10: number
    evento_p90: number
    evento_ja_vendido: number
    venda_p50: number
    venda_p10: number
    venda_p90: number
    venda_erro_tipico?: number | null
    ultimos_h_dias: number
    ano_anterior_venda: number
  }
  ano_anterior_face_total?: number
  ano_anterior_face_abertos?: number
  ano_anterior_top?: TopGmv[]
  ano_anterior_evento_total?: number
  ano_anterior_evento_abertos?: number
  ano_anterior_eventos_abertos_n?: number
}

export interface ErroModelo {
  MdAPE: number
}

export interface Qualidade {
  curto: Record<string, Record<string, any> & { cobertura_80: number }>
  longo: Record<string, Record<string, any> & { cobertura_80: number }>
  gmv: Record<string, { so_abertos?: ErroModelo; evento_so_abertos?: ErroModelo }>
}

/** previsão dia a dia por data da venda (nível dos últimos 7 dias; faixa e erro por antecedência k) */
export interface PrevisaoDiaria {
  d: string
  k: number
  p50: number
  p10: number
  p90: number
  erro: number
  face_p50: number
  face_p10: number
  face_p90: number
}

/** série de um gráfico do estudo (linhas, empilhado ou pontos) */
export interface SerieEstudo {
  nome: string
  valores: number[]
  tracejada?: boolean
  /** x próprio (gráfico de pontos) */
  x?: number[]
}

/**
 * Números fixos do estudo de inteligência de vendas (artefato `estudo.json.gz`
 * do analytics, servido junto com o painel). Mudam só quando o estudo é refeito.
 */
export interface EstudoPrevisoes {
  DATA_ESTUDO: string
  ANOS: string[]
  PANDEMIA: string[]
  CANAL: SerieEstudo[]
  /** ano, eventos, pagos (mi), janela (dias), % no dia, % 3 primeiros dias, preço médio, cortesias */
  ANO_A_ANO: Array<[string, number, number, number, number, number, number, number]>
  DIAS: number[]
  CURVA_PERIODO: SerieEstudo[]
  CURVA_PORTE: SerieEstudo[]
  /** estilo, eventos, % no dia, % última semana, % >30 dias, antecedência mediana (dias) */
  ESTILOS: Array<[string, number, number, number, number, number]>
  MOTIVO_VIRADA: { categorias: string[]; valores: number[] }
  RITMO_POS_VIRADA: { categorias: string[]; valores: number[] }
  /** nome, eventos, mediana geral, UF de menor venda, UF de maior venda, nº de UFs */
  ARTISTA_UF: Array<[string, number, number, number, number, number]>
  /** perfil, produtores, pagos por evento, % no dia, erro da projeção em D-7 */
  PERFIS_PRODUTOR: Array<[string, number, number, number, number]>
  ANTECEDENCIAS: number[]
  /** null onde o método não se aplica (comparáveis só a partir de D-30) */
  ERRO_POR_METODO: Array<{ nome: string; valores: Array<number | null> }>
  JANELAS_K: number[]
  ESTABILIDADE: SerieEstudo[]
  CALIBRACAO_LOTE: SerieEstudo[]
  /** pergunta, taxa-base, regra simples, modelo */
  LOTE_BRIER: Array<[string, string, string, string]>
  /** alerta, eventos alertados, acerto, vezes a taxa-base, decisão */
  ALERTAS: Array<[string, string, string, string, string]>
}

export interface PainelPrevisoes {
  gerado_em: string
  referencia: string
  dados_ate: string
  duracao_s?: number
  kpi: {
    eventos: number
    pagos: number
    receita: number
    proj30: number
    proj30_lo: number
    proj30_hi: number
    alertas: number
    lotes_acabando: number
  }
  eventos: PrevisaoEvento[]
  lotes: PrevisaoLote[]
  gmv: Record<string, GmvHorizonte>
  gmv_diario: Record<string, number>
  face_diario: Record<string, number>
  gmv_previsao_diaria?: PrevisaoDiaria[]
  qualidade: Qualidade
}

export interface DetalheEvento {
  referencia: string
  evento: PrevisaoEventoDetalhe
  lotes: PrevisaoLote[]
}

/* ── simulador (antes da abertura) ── */
/** grupo do modelo: o produtor já fez evento no mesmo local / tem histórico em outro local / é novo */
export type GrupoSimulador = 'produtor_local' | 'produtor' | 'novo'
export type QualidadeSimulador = Record<GrupoSimulador, { n: number; erro_mediano: number; cobertura: number }> & {
  geral?: { n: number; erro_mediano: number }
}
/** grupo dos comparáveis (só referência na tela) */
export type GrupoComparaveis = 'artista' | 'local' | 'geral'

export interface SimuladorOpcoes {
  dados_ate: string
  qualidade: QualidadeSimulador
  limites: { tiposIngresso: number; setores: number; lotes: number; janelaDias: number; nome: number }
  ufs: string[]
  cidades: Array<{ cidade: string; uf: string; eventos: number }>
  locais: Array<{ id: number; nome: string; cidade: string; uf: string; eventos: number }>
  estilos: string[]
  artistas: Array<{ nome: string; eventos: number }>
  produtores: Array<{ id: number; nome: string; eventos: number; uf: string }>
}

export interface EntradaSimulador {
  uf: string
  cidade: string
  localId: number | null
  estilo: string
  preco: number
  data: string
  artistas: string[]
  produtorId: number | null
  /** nome do evento: liga às edições anteriores do mesmo produtor */
  nome: string
  /** data prevista de abertura das vendas */
  abertura: string
  hora: number
  tiposIngresso: number
  setores: number
  lotes: number
}

export interface HistoricoProdutor {
  id: number
  nome: string
  eventos: number
  media: number | null
  ultimo: number | null
  maximo: number | null
  eventos_local: number
  media_local: number | null
  eventos_mesmo_nome: number
  media_mesmo_nome: number | null
}

export interface HistoricoArtista {
  nome: string
  eventos: number
  ufs: number
  mediana: number | null
  eventos_uf: number
  mediana_uf: number | null
}

export interface ResultadoSimulador {
  dados_ate: string
  qualidade: QualidadeSimulador
  artistas: HistoricoArtista[]
  produtor?: HistoricoProdutor | null
  suficiente: boolean
  grupo?: GrupoSimulador
  confianca?: 'média' | 'baixa'
  /** erro mediano do modelo no grupo, no teste */
  erro_tipico?: number
  publico?: FaixaGmv
  face?: FaixaGmv
  comparaveis_grupo?: GrupoComparaveis | null
  comparaveis_n?: number
  comparaveis?: Array<{
    id: number
    nome: string
    data: string
    cidade: string
    local: string
    pagos: number
    preco_medio: number
    escore: number
    motivos: string[]
    artistas: string[]
  }>
}
